<template>
  <div>
    <button v-if="!isDrawerOpen" class="menu-btn" @click="toggleDrawer">Media Panel</button>
    <div class="drawer-overlay" v-if="isDrawerOpen" @click="toggleDrawer"></div>
    <div class="drawer" :class="{ open: isDrawerOpen }">
      <button class="close-btn" @click="toggleDrawer">×</button>
      <div class="nav-bar">
        <div class="nav-left">
          <input type="file" multiple ref="fileInput" @change="mediaStore.handleFileUpload" />
          <button class="upload-btn" @click="onUploadImages">Upload Image</button>
        </div>
        <div class="nav-right">
          <button class="apply-btn" @click="mediaStore.applyChanges">Apply Changes</button>
          <button class="reset-btn" @click="confirmReset">Reset Selected</button>
        </div>
      </div>
      <div class="info-text">
        <h4>Supported image formats: JPEG, GIF, PNG, BMP and WEBP</h4>
        <h4>For best results use image size of 1000x800</h4>
      </div>
      <div class="images-container">
        <div
          v-for="img in mediaStore.images"
          :key="img.id"
          class="image-item"
          :class="{ selected: mediaStore.isSelected(img.id) }"
          @click="mediaStore.toggleSelect(img.id)"
        >
          <span class="delete-icon" @click.stop="mediaStore.deleteImage(img.id)">🗑</span>
          <img :src="img.image" :alt="'Image ' + img.id" />
        </div>
      </div>
    </div>

    <base-status-layout
      :operators="true"
      :in-progress-cards="inProgressCards"
      :ready-to-handout-cards="readyToHandoutCards"
      :carousel-images="mediaStore.selectedImages"
      :formatted-avg-collection-time="formattedAvgCollectionTime"
    />
  </div>
</template>

<script>
import { useMediaStore } from "@/store/media"
import { useOperatorStore } from "@/store/operator"
import { useSettingStore } from "@/store/settings"
import BaseStatusLayout from "@/core/components/layout/BaseStatusLayout"
import { mapState, mapActions } from "pinia"

export default {
  name: "Media",
  components: { BaseStatusLayout },
  data() {
    return {
      isDrawerOpen: false,
      dynamicImageUrl: "",
      avgCollectionTime: 60
    }
  },
  computed: {
    ...mapState(useOperatorStore, ["prescriptionCards"]),
    ...mapState(useSettingStore, ["settingsList"]),
    mediaStore() {
      return useMediaStore()
    },
    inProgressCards() {
      return this.prescriptionCards.filter((c) => [0, "0", 1, "1"].includes(c.current_status))
    },
    readyToHandoutCards() {
      return this.prescriptionCards.filter((c) => [2, "2"].includes(c.current_status))
    },
    formattedAvgCollectionTime() {
      return Math.ceil(this.avgCollectionTime / 60)
    }
  },
  methods: {
    ...mapActions(useOperatorStore, ["getPrescriptions"]),
    ...mapActions(useSettingStore, ["getSettingsList"]),
    toggleDrawer() {
      this.isDrawerOpen = !this.isDrawerOpen
    },
    handleStatusUpdate({ uuid, current_status }) {
      this.getPrescriptions()
      const updateCard = this.prescriptionCards.find((card) => card.uuid === uuid)
      if (updateCard) {
        this.$set(updateCard, "current_status", current_status)
      }
    },
    handleAvgCollectionTime(data) {
      if (data && data.average_collection_time > 0) {
        this.avgCollectionTime = data.average_collection_time
      }
    },
    async fetchSelectedImages() {
      await this.mediaStore.fetchSelectedImages()
    },
    confirmReset() {
      const userConfirmed = confirm("Are you sure you want to reset all selected images?")
      if (!userConfirmed) return
      this.mediaStore.resetSelected()
    },
    async onUploadImages() {
      await this.mediaStore.uploadImages();
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = null;
      }
    }
  },
  async mounted() {
    await this.getPrescriptions()
    await this.getSettingsList()
    await this.mediaStore.fetchImages()
    await this.fetchSelectedImages()
    this.$ws.connect()
    this.$ws.listen("UPDATE_MEDIA_SETTINGS", async (data) => {
      this.dynamicImageUrl = data.image_url || ""
      await this.fetchSelectedImages()
    })
    this.$ws.listen("AVG_COLLECTION_TIME", this.handleAvgCollectionTime)
    this.$ws.listen("STATUS_CHANGE", this.handleStatusUpdate)
    this.$ws.listen("SEND_CARD_TO_OPERATORS", this.handleStatusUpdate)
  },
  destroyed() {
    this.$ws.remove("UPDATE_MEDIA_SETTINGS")
    this.$ws.remove("AVG_COLLECTION_TIME")
    this.$ws.remove("STATUS_CHANGE")
    this.$ws.remove("SEND_CARD_TO_OPERATORS")
  }
}
</script>

<style scoped lang="scss">
.drawer {
  position: fixed;
  top: 0;
  left: -48%;
  width: 48%;
  height: 100%;
  background: #0a8544;
  transition: left 0.3s ease-in-out;
  z-index: 999;
  padding: 20px;
  overflow-y: auto;
}
.drawer.open {
  left: 0;
}
.nav-bar {
  margin-top: 2rem;
  border-bottom: 2px solid #fff;
  padding-bottom: 0.5rem;
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
}
.nav-left,
.nav-right {
  display: flex;
  gap: 10px;
}
.close-btn {
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  color: #fff;
  font-size: 24px;
  cursor: pointer;
}
.menu-btn {
  position: fixed;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  font-size: 26px;
  background: #0a8544;
  color: #fff;
  border: 2px solid black;
  padding: 250px 25px;
  border-radius: 5px;
  cursor: pointer;
  z-index: 1000;
  box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.2);
}
.images-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, 150px);
  gap: 1rem;
  border: 2px solid #fff;
  padding: 10px;
  border-radius: 5px;
}
.image-item {
  width: 150px;
  height: 150px;
  position: relative;
  background: #fff;
  border-radius: 5px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
}
.image-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.delete-icon {
  position: absolute;
  top: 5px;
  left: 5px;
  background: rgba(255, 0, 0, 0.7);
  color: #fff;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 14px;
}
.selected {
  border-color: #ffc107;
}
.reset-btn {
  background-color: #dc3545;
  color: #fff;
}
.info-text {
  color: #fff;
  margin-bottom: 15px;
}
</style>
